import type { JobStatus } from '../types/document'

/**
 * EVERY QUOTE YOU HAVE OUT, AND WHO HAS NOT ANSWERED.
 *
 * PROP-01. The screen exists for one question a contractor asks in November,
 * standing in a driveway: which of these forty houses still owes me an answer,
 * and how long have I been waiting.
 *
 * A DOCUMENT IS NOT A SECOND RECORD OF A JOB. The spec is explicit that an
 * estimate and a proposal are presentation options over one shared scope, not
 * duplicate financial records, so nothing here is stored: this is the job list
 * asked a different question. The quote's number, date and total already live
 * on the job, written at save time, and reading them a second way cannot
 * disagree with the job page the way a copy would.
 *
 * WHAT IS HONESTLY NOT HERE, AND SAYS SO.
 *
 *   Whether the customer opened it. The spec calls viewed tracked evidence,
 *   and there is no evidence: no portal, no delivery record, nothing in this
 *   product that a homeowner has ever touched. A screen showing Viewed with
 *   nothing behind it would be worse than one that shows nothing.
 *
 *   Expiry. The spec says expiry uses the configured timezone. There is no
 *   expiry field on a document and no configured timezone to read it in.
 *
 *   Sent. Nothing in this app has ever sent a message. Opening a share sheet
 *   is not a delivery and a download is not a customer who has read it, which
 *   ui/ShareSheet.tsx has always said out loud. So sent here means marked by a
 *   person, and the wording on the row never says more than that.
 */

/** The money the job list already carries, and nothing more. */
export interface DocMoney {
  kind: 'quote' | 'invoice'
  /** LP-1041. Empty until the quote has lines on it. */
  number: string
  /** The day written on the document, not the day the file was touched. */
  dated: string
  total: number
  paid: number
  balance: number
}

/** What this screen needs off a job. Deliberately not the whole summary. */
export interface DocRef {
  id: string
  customer: string
  address: string
  name: string
  status: JobStatus
  /** When a person last moved it. Empty on anything nobody has moved. */
  statusAt: string
  money?: DocMoney
}

/**
 * A JOB WITH A PRICE ON IT.
 *
 * Photographing a house is not quoting it. Most of what is in this app on a
 * Saturday is a picture somebody drew in a driveway and has not costed, and
 * putting those on a list of estimates would bury the eight that are real
 * under thirty that are not.
 */
export const hasDocument = (j: DocRef): boolean => !!j.money && j.money.total > 0

/*
 * THE STATES ARE THE ONES THE PRODUCT ALREADY HAS.
 *
 * types/document.ts carries four and a contractor says all four out loud. A
 * fifth invented here would be a state nothing can ever set, and a filter that
 * is always empty is a lie about the shape of the work.
 */
export type DocState = 'unsent' | 'waiting' | 'approved' | 'lost'

/*
 * TWO NAMES EACH, and the short one is not an abbreviation of the long one for
 * the sake of it. A filter reads best as the sentence a person would say out
 * loud; a pill in a 152px column beside forty others reads best as one word,
 * and the row underneath it already says how long and what to do about it.
 */
export const DOC_STATES: { id: DocState; label: string; short: string }[] = [
  { id: 'unsent', label: 'Not sent yet', short: 'Not sent' },
  { id: 'waiting', label: 'Waiting on an answer', short: 'Waiting' },
  { id: 'approved', label: 'Approved', short: 'Approved' },
  { id: 'lost', label: 'Lost', short: 'Lost' },
]

export function docState(j: DocRef): DocState {
  if (j.status === 'won') return 'approved'
  if (j.status === 'lost') return 'lost'
  if (j.status === 'quoted') return 'waiting'
  return 'unsent'
}

/**
 * WHAT A QUOTE CAN BECOME FROM HERE, AND ONLY THAT.
 *
 * The job list used to cycle the four states with one button, which is right
 * for a list about houses and wrong for anything about answers: cycling Lost
 * round to Not sent yet is not a thing anybody means to do, and the button
 * that did it read "Mark not sent yet", which is a state written as a
 * command and reads as a negation.
 *
 * Two screens ask this question - the estimates list and the job's own page -
 * so it is answered once, here, where it can be tested. Outcomes are named
 * from the person's side ("they approved it"), and there are never more than
 * two, because a control offering four things offers no opinion.
 */
export function moves(state: DocState): { to: JobStatus; label: string }[] {
  switch (state) {
    case 'unsent':
      return [
        { to: 'quoted', label: 'Mark it sent' },
        { to: 'lost', label: 'Mark it lost' },
      ]
    case 'waiting':
      return [
        { to: 'won', label: 'They approved it' },
        { to: 'lost', label: 'Mark it lost' },
      ]
    default:
      return [{ to: 'quoted', label: 'Put it back on the list' }]
  }
}


/**
 * WHOLE DAYS BETWEEN TWO PLAIN DATES.
 *
 * Built off the date parts rather than a Date's local calendar, for the reason
 * lib/visits.ts spells out: a quote dated the seventh reads as the sixth in
 * Chicago if you let a timestamp near midnight decide, and a list about how
 * long somebody has been waiting cannot be a day out.
 */
export function daysBetween(from: string, to: string): number | null {
  const a = from.slice(0, 10)
  const b = to.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return null
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  const ms = Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)
  return Math.round(ms / 86400000)
}

/**
 * THE DAY THE CLOCK STARTED ON THIS ONE.
 *
 * A job somebody marked has the moment they marked it, which is the honest
 * start of a wait. A job nobody has moved falls back to the date on the
 * document, and a document with no date at all has no clock, which is
 * different from a clock reading zero.
 */
export function since(j: DocRef): string {
  if (j.statusAt) return j.statusAt.slice(0, 10)
  return j.money?.dated ?? ''
}

export function age(j: DocRef, today: string): number | null {
  const from = since(j)
  if (!from) return null
  const n = daysBetween(from, today)
  return n === null ? null : Math.max(0, n)
}

/** How long, said the way a person says it. */
export function ageWording(days: number | null): string {
  if (days === null) return 'No date on it'
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 14) return `${days} days`
  if (days < 60) return `${Math.floor(days / 7)} weeks`
  return `${Math.floor(days / 30)} months`
}

/**
 * WHAT TO DO ABOUT IT, in the words a contractor would use.
 *
 * One sentence per row rather than a column of verbs, because the next thing
 * to do about an unpriced draft and a three week old quote are not the same
 * kind of thing and a single Next action column would flatten them.
 */
export function nextAction(j: DocRef, today: string): string {
  const state = docState(j)
  const days = age(j, today)
  if (state === 'approved') {
    if (j.money?.kind === 'invoice' && j.money.balance > 0) return 'Approved. The balance is still owed.'
    return 'Approved. Book the install.'
  }
  if (state === 'lost') return 'Closed out.'
  if (state === 'unsent') return 'Priced, and nobody has been told yet.'
  if (days === null) return 'Waiting on an answer.'
  if (days >= 14) return `Waiting ${ageWording(days).toLowerCase()}. Worth a phone call.`
  return `Waiting ${ageWording(days).toLowerCase()} on an answer.`
}

/** Longest wait first, because that is the one a season is lost to. */
export function byWaiting(rows: DocRef[], today: string): DocRef[] {
  return [...rows].sort((a, b) => {
    const av = age(a, today)
    const bv = age(b, today)
    if (av === null && bv === null) return 0
    // A document with no date on it is not old, it is unknown, so it sits at
    // the bottom rather than being sorted as though it were brand new.
    if (av === null) return 1
    if (bv === null) return -1
    return bv - av
  })
}

/**
 * THE ORDER THE SCREEN PROMISES.
 *
 * The heading says the top of this list is who to call, and sorting purely by
 * age broke that promise the first time it was looked at: a quote lost three
 * weeks ago landed above a live one from Tuesday, because a lost quote is
 * still old. So the states go in the order a person cares about them, and the
 * age sorts within each.
 *
 * Waiting and unsent are sorted longest first, because the oldest one is the
 * one about to be forgotten. Approved and lost are sorted newest first,
 * because nothing is owed on them and the only reason to look is to see what
 * just happened.
 */
const RANK: Record<DocState, number> = { waiting: 0, unsent: 1, approved: 2, lost: 3 }

export function byAttention(rows: DocRef[], today: string): DocRef[] {
  return [...rows].sort((a, b) => {
    const sa = docState(a)
    const sb = docState(b)
    if (RANK[sa] !== RANK[sb]) return RANK[sa] - RANK[sb]
    const av = age(a, today)
    const bv = age(b, today)
    // No date is not new and not old, it is unknown, so it sits at the bottom
    // of its own group rather than being sorted as though it were either.
    if (av === null && bv === null) return 0
    if (av === null) return 1
    if (bv === null) return -1
    const open = sa === 'waiting' || sa === 'unsent'
    return open ? bv - av : av - bv
  })
}

/** What is out there with no answer on it yet. The number of the screen. */
export function outstanding(rows: DocRef[]): number {
  return rows
    .filter((j) => docState(j) === 'waiting')
    .reduce((n, j) => n + (j.money?.total ?? 0), 0)
}

/** And how many of them there are, which the total alone does not say. */
export const waitingCount = (rows: DocRef[]): number =>
  rows.filter((j) => docState(j) === 'waiting').length

/**
 * The three things the spec asks this screen for that nothing behind it can
 * answer. Printed on the screen rather than left out of it, for the same
 * reason lib/visits.ts prints its own: a column of blanks reads as a bug, and
 * a column filled in with guesses reads as a fact.
 */
export const NOT_TRACKED: string[] = [
  'Whether the customer has opened it. There is no portal and no delivery record behind that.',
  'When it expires. A document carries no expiry date yet.',
  'Sent means a person marked it here. Nothing in this app has sent a message.',
]
